<!-- src/views/clinical/ClinicalHistoryDetail.vue -->
<template>
  <div class="clinical-history-detail">
    <!-- Back button and actions bar -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <router-link to="/clinical-histories" class="btn btn-outline-primary">
        <i class="bi bi-arrow-left me-2"></i>Volver
      </router-link>
      <div>
        <router-link :to="`/patients/${patient?.id}`" class="btn btn-outline-primary me-2" v-if="patient">
          <i class="bi bi-person me-1"></i> Perfil del Paciente
        </router-link>
        <router-link :to="`/clinical-histories/${clinicalHistoryId}/edit`" class="btn btn-primary">
          <i class="bi bi-pencil me-1"></i> Editar
        </router-link>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="d-flex justify-content-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="alert alert-danger">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ error }}
      <div class="mt-3">
        <router-link to="/clinical-histories" class="btn btn-outline-primary">
          <i class="bi bi-arrow-left me-2"></i>Volver a la lista de historias clínicas
        </router-link>
      </div>
    </div>

    <!-- History details -->
    <div v-else-if="clinicalHistory && patient" class="row">
      <!-- Main content column -->
      <div class="col-md-8">
        <div class="card mb-4">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">Historia Clínica</h5>
            <span class="text-muted small" v-if="lastUpdated">
              Actualizado: {{ lastUpdated }}
            </span>
          </div>
          <div class="card-body">
            <h2 class="mb-4 border-bottom pb-3">{{ patient.first_name }} {{ patient.last_name }}</h2>

            <!-- Historial de Tratamientos (ahora primero) -->
            <div class="mb-4">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="text-primary mb-0">Historial de Tratamientos</h5>
                <div>
                  <button class="btn btn-sm btn-outline-primary d-print-none" @click="showTreatmentModal = true">
                    <i class="bi bi-plus-circle me-1"></i> Añadir
                  </button>
                </div>
              </div>
              <div v-if="treatments.length === 0" class="text-muted fst-italic">
                No hay tratamientos registrados
              </div>
              <!-- Timeline view with treatment description rendered line by line preserving blank lines -->
              <div v-else class="treatment-timeline">
                <div v-for="(treatment, index) in treatments" :key="index" class="treatment-item">
                  <div class="treatment-date">{{ formatDate(treatment.date) }}</div>
                  <div class="treatment-content">
                    <div class="d-flex justify-content-between">
                      <h6 class="mb-2">{{ treatment.title }}</h6>
                      <div class="treatment-actions">
                        <button class="btn btn-sm btn-link text-primary" @click="editTreatment(index)">
                          <i class="bi bi-pencil"></i>
                        </button>
                        <button class="btn btn-sm btn-link text-danger" @click="confirmDeleteTreatment(index)">
                          <i class="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                    <!-- Se muestra el texto línea por línea; si la línea está vacía se renderiza un espacio no rompible -->
                    <div class="mb-2 treatment-description">
                      <p v-for="(line, lineIdx) in getDescriptionLines(treatment.description)" :key="lineIdx" class="mb-1">
                        {{ line || '\u00A0' }}
                      </p>
                    </div>
                    <div class="text-muted small">{{ treatment.doctor }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Historial Médico (segunda sección) -->
            <div class="mb-4">
              <h5 class="text-primary">Historial Médico</h5>
              <div class="card">
                <div class="card-body">
                  <!-- Consultation Reason -->
                  <div class="mb-3">
                    <h6 class="fw-bold">Motivo de la Consulta:</h6>
                    <div v-if="getTextLines(clinicalHistory.consultation_reason).length > 0" class="text-lines">
                      <p v-for="(line, index) in getTextLines(clinicalHistory.consultation_reason)" :key="index" class="mb-1">
                        {{ line }}
                      </p>
                    </div>
                    <p v-else class="text-muted fst-italic">No especificado</p>
                  </div>

                  <!-- Medical History -->
                  <div class="mb-3">
                    <h6 class="fw-bold">Antecedentes Médicos:</h6>
                    <div v-if="getTextLines(clinicalHistory.medical_history).length > 0" class="text-lines">
                      <p v-for="(line, index) in getTextLines(clinicalHistory.medical_history)" :key="index" class="mb-1">
                        {{ line }}
                      </p>
                    </div>
                    <p v-else class="text-muted fst-italic">No especificado</p>
                  </div>

                  <!-- Family Medical History -->
                  <div class="mb-3">
                    <h6 class="fw-bold">Antecedentes Familiares:</h6>
                    <div v-if="getTextLines(clinicalHistory.family_medical_history).length > 0" class="text-lines">
                      <p v-for="(line, index) in getTextLines(clinicalHistory.family_medical_history)" :key="index" class="mb-1">
                        {{ line }}
                      </p>
                    </div>
                    <p v-else class="text-muted fst-italic">No especificado</p>
                  </div>

                  <!-- Medications -->
                  <div class="mb-3">
                    <h6 class="fw-bold">Medicamentos:</h6>
                    <div v-if="getTextLines(clinicalHistory.medications).length > 0" class="text-lines">
                      <p v-for="(line, index) in getTextLines(clinicalHistory.medications)" :key="index" class="mb-1">
                        {{ line }}
                      </p>
                    </div>
                    <p v-else class="text-muted fst-italic">No especificado</p>
                  </div>

                  <!-- Allergies -->
                  <div>
                    <h6 class="fw-bold">Alergias:</h6>
                    <div v-if="getTextLines(clinicalHistory.allergies).length > 0" class="text-lines">
                      <p v-for="(line, index) in getTextLines(clinicalHistory.allergies)" :key="index" class="mb-1">
                        {{ line }}
                      </p>
                    </div>
                    <p v-else class="text-muted fst-italic">No especificado</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Historial Dental (tercera sección) -->
            <div class="mb-4">
              <h5 class="text-primary">Historial Dental</h5>
              <div class="card">
                <div class="card-body">
                  <!-- Dental Medical History -->
                  <div class="mb-3">
                    <h6 class="fw-bold">Antecedentes Odontológicos:</h6>
                    <div v-if="getTextLines(clinicalHistory.dental_history).length > 0" class="text-lines">
                      <p v-for="(line, index) in getTextLines(clinicalHistory.dental_history)" :key="index" class="mb-1">
                        {{ line }}
                      </p>
                    </div>
                    <p v-else class="text-muted fst-italic">No especificado</p>
                  </div>

                  <!-- Orthodontic Treatment -->
                  <div class="mb-3">
                    <h6 class="fw-bold">Tratamiento de Ortodoncia previo:</h6>
                    <p>{{ clinicalHistory.previous_orthodontic_treatment === 'si' ? 'Sí' :
                      (clinicalHistory.previous_orthodontic_treatment === 'no' ? 'No' : 'No especificado') }}</p>
                  </div>

                  <!-- Gum Bleeding -->
                  <div class="mb-3">
                    <h6 class="fw-bold">Sangrado de encías:</h6>
                    <p>{{ clinicalHistory.gum_bleeding === 'si' ? 'Sí' : (clinicalHistory.gum_bleeding === 'no' ? 'No' :
                      'No especificado') }}</p>
                  </div>

                  <!-- Smoking -->
                  <div class="mb-3">
                    <h6 class="fw-bold">Fumador:</h6>
                    <p>{{ clinicalHistory.smoking === 'si' ? 'Sí' : (clinicalHistory.smoking === 'no' ? 'No' : 'No especificado') }} </p>
                  </div>

                  <!-- Teeth Grinding -->
                  <div class="mb-3">
                    <h6 class="fw-bold">Aprieta los dientes:</h6>
                    <div v-if="getTextLines(clinicalHistory.teeth_grinding).length > 0" class="text-lines">
                      <p v-for="(line, index) in getTextLines(clinicalHistory.teeth_grinding)" :key="index" class="mb-1">
                        {{ line }}
                      </p>
                    </div>
                    <p v-else class="text-muted fst-italic">No especificado</p>
                  </div>

                  <!-- Habits -->
                  <div class="mb-3">
                    <h6 class="fw-bold">Hábitos:</h6>
                    <p>{{ clinicalHistory.habits ? formatHabits(clinicalHistory.habits) : 'No especificado' }}</p>
                  </div>

                  <!-- Respiratory Problems -->
                  <div class="mb-3">
                    <h6 class="fw-bold">Problemas respiratorios:</h6>
                    <div v-if="getTextLines(clinicalHistory.respiratory_problems).length > 0" class="text-lines">
                      <p v-for="(line, index) in getTextLines(clinicalHistory.respiratory_problems)" :key="index" class="mb-1">
                        {{ line }}
                      </p>
                    </div>
                    <p v-else class="text-muted fst-italic">No especificado</p>
                  </div>

                  <!-- Previous Dental Surgery -->
                  <div class="mb-3">
                    <h6 class="fw-bold">Cirugía dental previa:</h6>
                    <p>{{ clinicalHistory.previous_dental_surgery === 'si' ? 'Sí' :
                      (clinicalHistory.previous_dental_surgery === 'no' ? 'No' : 'No especificado') }}</p>
                  </div>

                  <!-- Local Anesthetic Problems -->
                  <div class="mb-3">
                    <h6 class="fw-bold">Problemas con anestésicos locales:</h6>
                    <p>{{ clinicalHistory.local_anesthetic_problems === 'si' ? 'Sí' :
                      (clinicalHistory.local_anesthetic_problems === 'no' ? 'No' : 'No especificado') }}</p>
                  </div>

                  <!-- Coagulation Problems -->
                  <div class="mb-3">
                    <h6 class="fw-bold">Problemas de coagulación:</h6>
                    <p>{{ clinicalHistory.coagulation_problems === 'si' ? 'Sí' : (clinicalHistory.coagulation_problems === 'no' ? 'No' : 'No especificado') }}</p>
                  </div>

                  <!-- Tooth Pain -->
                  <div class="mb-3">
                    <h6 class="fw-bold">Dolor o molestia dental:</h6>
                    <div v-if="getTextLines(clinicalHistory.tooth_pain).length > 0" class="text-lines">
                      <p v-for="(line, index) in getTextLines(clinicalHistory.tooth_pain)" :key="index" class="mb-1">
                        {{ line }}
                      </p>
                    </div>
                    <p v-else class="text-muted fst-italic">No especificado</p>
                  </div>

                  <!-- Joint Pain -->
                  <div class="mb-3">
                    <h6 class="fw-bold">Dolor articular:</h6>
                    <div v-if="getTextLines(clinicalHistory.joint_pain).length > 0" class="text-lines">
                      <p v-for="(line, index) in getTextLines(clinicalHistory.joint_pain)" :key="index" class="mb-1">
                        {{ line }}
                      </p>
                    </div>
                    <p v-else class="text-muted fst-italic">No especificado</p>
                  </div>

                  <!-- Oral Cavity Pain -->
                  <div class="mb-3">
                    <h6 class="fw-bold">Dolor en cavidad bucal:</h6>
                    <div v-if="getTextLines(clinicalHistory.oral_cavity_pain).length > 0" class="text-lines">
                      <p v-for="(line, index) in getTextLines(clinicalHistory.oral_cavity_pain)" :key="index" class="mb-1">
                        {{ line }}
                      </p>
                    </div>
                    <p v-else class="text-muted fst-italic">No especificado</p>
                  </div>

                  <!-- Diagnostics -->
                  <div class="mb-3">
                    <h6 class="fw-bold">Diagnóstico:</h6>
                    <div v-if="getTextLines(clinicalHistory.diagnostic).length > 0" class="text-lines">
                      <p v-for="(line, index) in getTextLines(clinicalHistory.diagnostic)" :key="index" class="mb-1">
                        {{ line }}
                      </p>
                    </div>
                    <p v-else class="text-muted fst-italic">No especificado</p>
                  </div>

                  <!-- Clinical notes section -->
                  <div>
                    <h6 class="fw-bold">Notas:</h6>
                    <div v-if="getNotesLines.length > 0" class="clinical-notes p-3 bg-light rounded">
                      <p v-for="(line, index) in getNotesLines" :key="index" class="mb-1">
                        {{ line }}
                      </p>
                    </div>
                    <p v-else class="text-muted fst-italic">No hay notas clínicas registradas</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Clinical Images section -->
            <div class="mb-4">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="text-primary">Imágenes Clínicas</h5>
                <button class="btn btn-sm btn-outline-primary" @click="showImageUploader = true">
                  <i class="bi bi-cloud-upload me-1"></i> Subir imágenes
                </button>
              </div>
              <!-- Image Gallery Component -->
              <ImageGallery :clinicalHistoryId="clinicalHistoryId" :canDelete="true" ref="imageGallery" />
            </div>
          </div>
        </div>
      </div>

      <!-- Right sidebar -->
      <div class="col-md-4">
        <!-- Patient information card -->
        <div class="card mb-4">
          <div class="card-header">
            <h5 class="mb-0">Información del Paciente</h5>
          </div>
          <div class="card-body">
            <div class="d-flex align-items-center mb-3">
              <div class="avatar-placeholder me-3">
                {{ getInitials(patient.first_name, patient.last_name) }}
              </div>
              <div>
                <h5 class="mb-0">{{ patient.first_name }} {{ patient.last_name }}</h5>
                <p v-if="patient.fecha_nacimiento" class="text-muted mb-0">
                  {{ formatAge(patient.fecha_nacimiento) }} años
                </p>
              </div>
            </div>
            <div class="patient-info">
              <div class="info-item" v-if="patient.telefono">
                <div class="info-label">
                  <i class="bi bi-telephone me-2"></i> Teléfono
                </div>
                <div class="info-value">{{ patient.telefono }}</div>
              </div>
              <div class="info-item" v-if="patient.fecha_nacimiento">
                <div class="info-label">
                  <i class="bi bi-calendar me-2"></i> Fecha de nacimiento
                </div>
                <div class="info-value">{{ formatDate(patient.fecha_nacimiento) }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Odontogram card -->
        <div class="card mb-4">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">Odontograma</h5>
            <div>
              <button v-if="hasOdontogram" class="btn btn-sm btn-outline-primary" @click="viewOdontogram">
                Ver completo
              </button>
              <button v-else class="btn btn-sm btn-primary" @click="createOdontogram">
                <i class="bi bi-plus-circle me-1"></i> Crear
              </button>
            </div>
          </div>
          <div class="card-body">
            <div v-if="hasOdontogram">
              <OdontogramPreview :odontogramId="odontogram.id" />
            </div>
            <div v-else class="text-center text-muted py-3">
              <i class="bi bi-grid-3x3 d-block display-4 mb-3"></i>
              <p>No hay odontograma registrado para este paciente.</p>
            </div>
          </div>
        </div>

        <!-- Clinical Exam card -->
        <div class="card mb-4">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">Examen Clínico</h5>
            <div>
              <button v-if="hasClinicalExam" class="btn btn-sm btn-outline-primary" @click="viewClinicalExam">
                Ver completo
              </button>
              <button v-else class="btn btn-sm btn-primary" @click="createClinicalExam">
                <i class="bi bi-plus-circle me-1"></i> Crear
              </button>
            </div>
          </div>
          <div class="card-body">
            <div v-if="hasClinicalExam">
              <div class="row g-2">
                <div class="col-6">
                  <strong>Perfil:</strong>
                  <div v-if="getTextLines(clinicalExam.profile).length > 0" class="text-lines">
                    <p v-for="(line, index) in getTextLines(clinicalExam.profile)" :key="index" class="mb-1 text-muted">
                      {{ line }}
                    </p>
                  </div>
                  <p v-else class="text-muted fst-italic">No especificado</p>
                </div>
                <div class="col-6">
                  <strong>Cierre Labial:</strong>
                  <div v-if="getTextLines(clinicalExam.lip_closure).length > 0" class="text-lines">
                    <p v-for="(line, index) in getTextLines(clinicalExam.lip_closure)" :key="index" class="mb-1 text-muted">
                      {{ line }}
                    </p>
                  </div>
                  <p v-else class="text-muted fst-italic">No especificado</p>
                </div>
                <div class="col-6">
                  <strong>Análisis Frontal:</strong>
                  <div v-if="getTextLines(clinicalExam.frontal_analysis).length > 0" class="text-lines">
                    <p v-for="(line, index) in getTextLines(clinicalExam.frontal_analysis)" :key="index" class="mb-1 text-muted">
                      {{ line }}
                    </p>
                  </div>
                  <p v-else class="text-muted fst-italic">No especificado</p>
                </div>
                <div class="col-6">
                  <strong>Altura de Sonrisa:</strong>
                  <div v-if="getTextLines(clinicalExam.smile_height).length > 0" class="text-lines">
                    <p v-for="(line, index) in getTextLines(clinicalExam.smile_height)" :key="index" class="mb-1 text-muted">
                      {{ line }}
                    </p>
                  </div>
                  <p v-else class="text-muted fst-italic">No especificado</p>
                </div>
              </div>
            </div>
            <div v-else class="text-center text-muted py-3">
              <i class="bi bi-clipboard-pulse d-block display-4 mb-3"></i>
              <p>No hay examen clínico registrado para este paciente.</p>
            </div>
          </div>
        </div>

        <!-- Appointments card -->
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">Citas Recientes</h5>
            <router-link :to="`/appointments/new?patient=${patient.id}`" class="btn btn-sm btn-primary">
              <i class="bi bi-plus-circle me-1"></i> Nueva
            </router-link>
          </div>
          <div class="card-body p-0">
            <div v-if="appointments.length === 0" class="text-center py-3">
              <p class="text-muted mb-0">No hay citas registradas</p>
            </div>
            <ul v-else class="list-group list-group-flush">
              <li v-for="appointment in appointments" :key="appointment.id" class="list-group-item">
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <div class="fw-semibold">{{ formatDate(appointment.fecha) }}</div>
                    <div>{{ formatTime(appointment.hora) }} - {{ appointment.tipo_tratamiento }}</div>
                  </div>
                  <span :class="getStatusClass(appointment.estado)" class="badge rounded-pill">
                    {{ appointment.estado }}
                  </span>
                </div>
              </li>
            </ul>
          </div>
          <div class="card-footer bg-white">
            <router-link :to="`/appointments?patient=${patient.id}`" class="btn btn-sm btn-outline-primary w-100">
              Ver todas las citas
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- Image Upload Modal -->
    <div class="modal fade" :class="{ 'show d-block': showImageUploader }" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Subir imágenes clínicas</h5>
            <button type="button" class="btn-close" @click="showImageUploader = false"></button>
          </div>
          <div class="modal-body">
            <ImageUploader :clinicalHistoryId="clinicalHistoryId" @upload-complete="onUploadComplete" />
          </div>
        </div>
      </div>
    </div>

    <!-- Modal backdrop -->
    <div v-if="showImageUploader" class="modal-backdrop fade show"></div>

    <!-- Treatment Modal -->
    <div class="modal fade" :class="{ show: showTreatmentModal, 'd-block': showTreatmentModal }" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editingTreatmentIndex === null ? 'Añadir Tratamiento' : 'Editar Tratamiento' }}</h5>
            <button type="button" class="btn-close" @click="cancelTreatmentEdit"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label for="treatmentDate" class="form-label">Fecha</label>
              <input type="date" class="form-control" id="treatmentDate" v-model="newTreatment.date">
            </div>
            <div class="mb-3">
              <label for="treatmentTitle" class="form-label">Título</label>
              <input type="text" class="form-control" id="treatmentTitle" v-model="newTreatment.title">
            </div>
            <div class="mb-3">
              <label for="treatmentDescription" class="form-label">Descripción</label>
              <textarea class="form-control" id="treatmentDescription" rows="3" v-model="newTreatment.description"></textarea>
            </div>
            <div class="mb-3">
              <label for="treatmentDoctor" class="form-label">Doctor</label>
              <input type="text" class="form-control" id="treatmentDoctor" v-model="newTreatment.doctor">
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="cancelTreatmentEdit">Cancelar</button>
            <button type="button" class="btn btn-primary" @click="saveTreatment" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Guardar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div class="modal fade" :class="{ show: showDeleteModal, 'd-block': showDeleteModal }" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirmar eliminación</h5>
            <button type="button" class="btn-close" @click="showDeleteModal = false"></button>
          </div>
          <div class="modal-body">
            <p>¿Estás seguro de que deseas eliminar este tratamiento?</p>
            <p class="fw-bold">
              {{ deletingTreatmentIndex !== null && treatments[deletingTreatmentIndex] ? treatments[deletingTreatmentIndex].title : '' }}
            </p>
            <p class="text-danger">
              <i class="bi bi-exclamation-triangle me-2"></i>Esta acción no se puede deshacer.
            </p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showDeleteModal = false">Cancelar</button>
            <button type="button" class="btn btn-danger" @click="deleteTreatment" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Eliminar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal backdrops for treatment modals -->
    <div v-if="showTreatmentModal || showDeleteModal" class="modal-backdrop fade show"></div>
  </div>
</template>

<script>
import { clinicalHistoryService, patientService, appointmentService, odontogramService, clinicalExamService } from '@/services/api';
import { formatDateForDisplay, calculateAge, formatTime, formatDateForAPI, formatDateForInput, getTodayForInput } from '@/utils/dateUtils';
import OdontogramPreview from '@/components/odontogram/OdontogramPreview.vue';
import ImageGallery from '@/components/clinical/ImageGallery.vue';
import ImageUploader from '@/components/clinical/ImageUploader.vue';

export default {
  name: 'ClinicalHistoryDetail',
  components: {
    OdontogramPreview,
    ImageGallery,
    ImageUploader,
  },
  data() {
    return {
      clinicalHistoryId: null,
      clinicalHistory: null,
      clinicalExam: null,
      patient: null,
      odontogram: null,
      appointments: [],
      treatments: [],
      loading: true,
      error: null,
      showImageUploader: false,
      lastUpdated: null,
      showTreatmentModal: false,
      showDeleteModal: false,
      saving: false,
      editingTreatmentIndex: null,
      deletingTreatmentIndex: null,
      newTreatment: {
        date: new Date().toISOString().split('T')[0],
        title: '',
        description: '',
        doctor: ''
      }
    };
  },
  computed: {
    hasOdontogram() {
      return this.odontogram !== null;
    },
    hasClinicalExam() {
      return this.clinicalExam !== null;
    },
    getNotesLines() {
      if (!this.displayNotes) return [];
      return this.displayNotes.split(/\r?\n/).filter(line => line.trim() !== '');
    },
    displayNotes() {
      if (!this.clinicalHistory || !this.clinicalHistory.notes) return '';
      let notesContent = this.clinicalHistory.notes;
      const treatmentsRegex = /\[\s*{"date":[^\]]*\]\s*/;
      notesContent = notesContent.replace(treatmentsRegex, '').trim();
      return notesContent;
    }
  },
  created() {
    this.clinicalHistoryId = this.$route.params.id;
    this.fetchData();
  },
  methods: {
    // Método que divide el texto en líneas (para otros casos) filtrando vacíos
    getTextLines(text) {
      if (!text) return [];
      return text.split(/\r?\n/).filter(line => line.trim() !== '');
    },
    // Método para dividir el texto en líneas sin filtrar las vacías (usado en tratamientos)
    getDescriptionLines(description) {
      if (!description) return ['No especificado'];
      return description.split(/\r?\n/);
    },
    onUploadComplete() {
      this.showImageUploader = false;
      if (this.$refs.imageGallery) {
        this.$refs.imageGallery.fetchImages();
      }
    },
    async fetchData() {
      try {
        this.loading = true;
        const historyResponse = await clinicalHistoryService.get(this.clinicalHistoryId);
        this.clinicalHistory = historyResponse.data;
        if (this.clinicalHistory.updated_at) {
          this.lastUpdated = this.formatDate(this.clinicalHistory.updated_at);
        }
        this.parseTreatments();
        if (this.clinicalHistory && this.clinicalHistory.patient) {
          const patientResponse = await patientService.get(this.clinicalHistory.patient);
          this.patient = patientResponse.data;
          const [odontogramResponse, examResponse, appointmentsResponse] = await Promise.all([
            odontogramService.getByClinicalHistory(this.clinicalHistoryId),
            clinicalExamService.getByClinicalHistory(this.clinicalHistoryId),
            appointmentService.getByPatient(this.patient.id)
          ]);
          if (odontogramResponse.data?.length > 0) {
            this.odontogram = odontogramResponse.data[0];
          }
          if (examResponse.data?.length > 0) {
            this.clinicalExam = examResponse.data[0];
          }
          this.appointments = appointmentsResponse.data
            .sort((a, b) => new Date(b.fecha) - new Date(a.fecha))
            .slice(0, 3);
        }
      } catch (error) {
        console.error('Error loading clinical history details:', error);
        this.error = 'No se pudo cargar la historia clínica. Por favor, intenta de nuevo.';
      } finally {
        this.loading = false;
      }
    },
    async fetchOdontogram() {
      try {
        const response = await odontogramService.getByClinicalHistory(this.clinicalHistoryId);
        if (response.data && response.data.length > 0) {
          this.odontogram = response.data[0];
        }
      } catch (error) {
        console.error('Error fetching odontogram:', error);
      }
    },
    async fetchClinicalExam() {
      try {
        const response = await clinicalExamService.getByClinicalHistory(this.clinicalHistoryId);
        if (response.data && response.data.length > 0) {
          this.clinicalExam = response.data[0];
        }
      } catch (error) {
        console.error('Error fetching clinical exam:', error);
      }
    },
    viewClinicalExam() {
      if (this.clinicalExam) {
        this.$router.push(`/clinical-exam/${this.clinicalExam.id}`);
      }
    },
    createClinicalExam() {
      this.$router.push(`/clinical-exam/new?clinical_history=${this.clinicalHistoryId}`);
    },
    async fetchAppointments() {
      try {
        const response = await appointmentService.getByPatient(this.patient.id);
        this.appointments = response.data
          .sort((a, b) => new Date(b.fecha) - new Date(a.fecha))
          .slice(0, 3);
      } catch (error) {
        console.error('Error fetching appointments:', error);
      }
    },
    parseTreatments() {
      this.treatments = [];
      if (this.clinicalHistory && this.clinicalHistory.notes) {
        let notesContent = this.clinicalHistory.notes;
        const treatmentsRegex = /\[\s*{"date":[^\]]*\]\s*/;
        const treatmentsMatch = notesContent.match(treatmentsRegex);
        if (treatmentsMatch && treatmentsMatch[0]) {
          try {
            this.treatments = JSON.parse(treatmentsMatch[0]);
            notesContent = notesContent.replace(treatmentsRegex, '').trim();
          } catch (e) {
            console.error('Error parsing treatments:', e);
          }
        }
        this.displayNotes = notesContent;
      }
    },
    // Edit treatment methods
    editTreatment(index) {
      this.editingTreatmentIndex = index;
      const treatment = this.treatments[index];
      this.newTreatment = {
        date: treatment.date,
        title: treatment.title,
        description: treatment.description,
        doctor: treatment.doctor
      };
      this.showTreatmentModal = true;
    },
    cancelTreatmentEdit() {
      this.showTreatmentModal = false;
      this.editingTreatmentIndex = null;
      this.newTreatment = {
        date: new Date().toISOString().split('T')[0],
        title: '',
        description: '',
        doctor: ''
      };
    },
    confirmDeleteTreatment(index) {
      this.deletingTreatmentIndex = index;
      this.showDeleteModal = true;
    },
    async deleteTreatment() {
      if (this.deletingTreatmentIndex === null) return;
      this.saving = true;
      try {
        this.treatments.splice(this.deletingTreatmentIndex, 1);
        await this.updateTreatmentsInNotes();
        await this.fetchData();
        this.showDeleteModal = false;
        this.deletingTreatmentIndex = null;
      } catch (error) {
        console.error('Error deleting treatment:', error);
        this.error = 'No se pudo eliminar el tratamiento. Por favor, intenta de nuevo.';
      } finally {
        this.saving = false;
      }
    },
    async saveTreatment() {
      this.saving = true;
      try {
        if (this.editingTreatmentIndex !== null) {
          this.treatments[this.editingTreatmentIndex] = { ...this.newTreatment };
        } else {
          this.treatments.unshift({ ...this.newTreatment });
        }
        this.treatments.sort((a, b) => new Date(b.date) - new Date(a.date));
        await this.updateTreatmentsInNotes();
        await this.fetchData();
        this.cancelTreatmentEdit();
      } catch (error) {
        console.error('Error saving treatment:', error);
        this.error = 'No se pudo guardar el tratamiento. Por favor, intenta de nuevo.';
      } finally {
        this.saving = false;
      }
    },
    async addTreatment() {
      return this.saveTreatment();
    },
    async updateTreatmentsInNotes() {
      let notes = this.clinicalHistory.notes || '';
      if (notes.includes('[{"date":')) {
        const start = notes.indexOf('[{"date":');
        const end = notes.indexOf('}]', start) + 2;
        notes = notes.replace(notes.substring(start, end), '').trim();
      }
      if (notes.trim() && this.treatments.length > 0) {
        notes += '\n\n';
      }
      if (this.treatments.length > 0) {
        notes += JSON.stringify(this.treatments);
      }
      await clinicalHistoryService.update(this.clinicalHistoryId, {
        ...this.clinicalHistory,
        notes
      });
    },
    viewOdontogram() {
      if (this.odontogram) {
        this.$router.push(`/odontograms/${this.odontogram.id}`);
      }
    },
    createOdontogram() {
      this.$router.push(`/odontograms/new?clinical_history=${this.clinicalHistoryId}`);
    },
    getInitials(firstName, lastName) {
      const first = firstName ? firstName.charAt(0).toUpperCase() : '';
      const last = lastName ? lastName.charAt(0).toUpperCase() : '';
      return `${first}${last}`;
    },
    formatDate(dateString) {
      return formatDateForDisplay(dateString);
    },
    formatTime(timeString) {
      return formatTime(timeString);
    },
    formatHabits(habitsString) {
      if (!habitsString) return '';
      let formattedHabits = habitsString.replace(/_/g, ' ');
      formattedHabits = formattedHabits.replace(/,/g, ', ');
      return formattedHabits.split(', ')
        .map(habit => habit.charAt(0).toUpperCase() + habit.slice(1))
        .join(', ');
    },
    formatAge(dateString) {
      return calculateAge(dateString);
    },
    getStatusClass(status) {
      const statusClasses = {
        'programada': 'bg-info',
        'completada': 'bg-success',
        'cancelada': 'bg-danger',
        'reprogramada': 'bg-warning'
      };
      return statusClasses[status] || 'bg-secondary';
    }
  }
};
</script>

<style scoped>
.avatar-placeholder {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: bold;
  color: #495057;
}
.patient-info {
  margin-top: 1rem;
}
.info-item {
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}
.info-item:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}
.info-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
}
.treatment-timeline {
  position: relative;
  padding-left: 2rem;
}
.treatment-timeline::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 7px;
  width: 2px;
  background-color: #e9ecef;
}
.treatment-item {
  position: relative;
  margin-bottom: 1.5rem;
}
.treatment-item:last-child {
  margin-bottom: 0;
}
.treatment-date {
  font-weight: 500;
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 0.5rem;
}
.treatment-date::before {
  content: '';
  position: absolute;
  top: 5px;
  left: -2rem;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #0d6efd;
}
.treatment-content {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 0.375rem;
  border-left: 3px solid #0d6efd;
}
.treatment-actions {
  display: flex;
  gap: 0.25rem;
}
.treatment-description p {
  margin-bottom: 0.25rem;
  line-height: 1.5;
}
.treatment-description p:last-child {
  margin-bottom: 0;
}
.text-lines p {
  margin-bottom: 0.25rem;
  line-height: 1.5;
}
.text-lines p:last-child {
  margin-bottom: 0;
}
.modal {
  background-color: rgba(0, 0, 0, 0.5);
}
</style>
